import React, { useMemo } from 'react'
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useSelector } from 'react-redux'
import { digitalAppApi } from '../../store/services/digitalAppApi'
import usePdfBlobUrl from '../../hooks/usePdfBlobUrl'
import { buildAlbaranPrintParams } from '../../utils/buildFacturaPrintParams'
import { buildAlbaranFilename } from '../../utils/buildAlbaranFilename'
import IvaPriceSwitch from '../IvaPriceSwitch/IvaPriceSwitch'

export default function AlbaranPdfPanel({
  orderId,
  tradeName,
  order,
  title = 'Print Albaran',
  onClose,
  showClose = false,
  sx,
}) {
  const priceWithIva = useSelector((state) => state.contextUI.PriceIVA)

  const albaranQueryArgs = useMemo(
    () => buildAlbaranPrintParams(orderId, tradeName, priceWithIva),
    [orderId, tradeName, priceWithIva],
  )

  const {
    data: albaranData,
    isLoading: isLoadingAlbaran,
    isError: isErrorAlbaran,
    isSuccess,
  } = digitalAppApi.useGetAlbaranByIdQuery(albaranQueryArgs, {
    skip: !albaranQueryArgs?.id,
  })

  const pdfBlobUrl = usePdfBlobUrl(albaranData, isSuccess)
  const downloadFilename = buildAlbaranFilename(
    order || { _id: orderId, tradeName },
    tradeName,
  )

  const handleDownloadServerPdf = () => {
    if (!pdfBlobUrl) return
    const link = document.createElement('a')
    link.href = pdfBlobUrl
    link.download = downloadFilename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleOpenServerPdf = () => {
    if (pdfBlobUrl) window.open(pdfBlobUrl, '_blank')
  }

  if (!orderId) return null

  return (
    <Box sx={sx}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography id="albaran-modal-title" variant="h6" component="h2" gutterBottom>
          {title}
        </Typography>
        {showClose && onClose && (
          <IconButton onClick={onClose} className="driver-comments-modal__close">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Box sx={{ mb: 2 }}>
        <IvaPriceSwitch compact sx={{ mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Order ID: {orderId?.slice(-6) || 'N/A'}
        </Typography>

        <Typography
          variant="body2"
          color={
            isLoadingAlbaran
              ? 'warning.main'
              : isErrorAlbaran
                ? 'error.main'
                : pdfBlobUrl
                  ? 'success.main'
                  : 'text.secondary'
          }
        >
          PDF Status:
          {isLoadingAlbaran ? (
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress color="warning" size={14} /> Loading...
            </Box>
          ) : isErrorAlbaran ? (
            ' Error fetching PDF'
          ) : pdfBlobUrl ? (
            ' Ready'
          ) : (
            ' Waiting for data'
          )}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 2 }}>
        {pdfBlobUrl && !isLoadingAlbaran && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={handleDownloadServerPdf} sx={{ minWidth: 120 }}>
              Download PDF
            </Button>
            <Button variant="outlined" onClick={handleOpenServerPdf} sx={{ minWidth: 120 }}>
              Open in New Tab
            </Button>
          </Box>
        )}

        {isErrorAlbaran && (
          <Typography color="error">Error: Failed to fetch document.</Typography>
        )}
      </Box>

      {pdfBlobUrl && !isLoadingAlbaran && (
        <Box
          sx={{
            height: { xs: '60dvh', sm: '70dvh' },
            width: '100%',
            mt: 2,
            border: '1px solid #ccc',
          }}
        >
          <iframe
            src={pdfBlobUrl}
            title="Server Albaran PDF Viewer"
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          />
        </Box>
      )}
    </Box>
  )
}
